import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Typography, CircularProgress, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem,
  Card, CardContent, IconButton, Chip, FormControl, InputLabel, Tooltip
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import SaveIcon from '@mui/icons-material/Save';
import Swal from "sweetalert2";
import apiClient from "@/api/client";
import { getPreferences } from "@/api/preferencesService";

const statusOptions = ["Approved", "Pending", "Rejected", "Completed"];

const capitalizeStatus = (status) => {
  if (!status) return '';
  const matched = statusOptions.find(
    opt => opt.toLowerCase() === status.toLowerCase()
  );
  return matched || status;
};

const formatMealType = (mealType) => {
  if (!mealType) return 'N/A';
  return mealType === 'breakfast' ? 'Breakfast' : 'Lunch';
};

const formatSourceType = (sourceType) => {
  if (!sourceType) return 'N/A';
  const types = {
    'direct': 'Day Dana',
    'annual': 'Annual Dana',
    'standby': 'Standby Dana'
  };
  return types[sourceType] || sourceType;
};

const formatReferenceNo = (referenceNo, rowData) => {
  // Case 1: Annual request with a valid parent reference number
  if (rowData?.source_type === 'annual' && rowData?.annual_reference_no) {
    const baseRef = rowData.annual_reference_no;
    // If it already contains a prefix (e.g., YB-2026-0001), return as is
    if (baseRef && baseRef.includes('-')) {
      return baseRef;
    }
    // Otherwise add prefix (though annual parent should already have it)
    const prefix = rowData.meal_type === 'breakfast' ? 'YB-' : 'YL-';
    return `${prefix}${baseRef}`;
  }

  // Case 2: Non‑annual or missing annual_reference_no – fallback to original logic
  if (!referenceNo && referenceNo !== '') return 'N/A';
  if (referenceNo && referenceNo.includes('-')) return referenceNo;
  
  const mealType = rowData?.meal_type;
  const sourceType = rowData?.source_type;
  if (!mealType || !referenceNo) return referenceNo || 'N/A';
  
  const prefix = sourceType === 'annual' 
    ? (mealType === 'breakfast' ? 'YB-' : 'YL-')
    : (mealType === 'breakfast' ? 'B-' : 'L-');
  return `${prefix}${referenceNo}`;
};

const formatDateForInput = (dateValue, fieldName) => {
  if (!dateValue) return '';
  
  try {
    // Handle string dates in various formats (similar to formatDate function)
    if (typeof dateValue === 'string') {
      // Check if it's already in YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      
      // Check if it has time component
      if (dateValue.includes('T') || dateValue.includes(' ')) {
        // Extract just the date part
        const datePart = dateValue.split('T')[0] || dateValue.split(' ')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
          return datePart;
        }
      }
    }
    
    // Try to parse the date
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    
    // Format as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    // For date-only fields like dana_event_date and date
    if (fieldName === 'dana_event_date' || fieldName === 'date') {
      return `${year}-${month}-${day}`;
    }
    
    // For datetime fields like created_at, updated_at
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (error) {
    return '';
  }
};

const getInputTypeForField = (fieldName, dateFields) => {
  if (!dateFields.includes(fieldName)) return 'text';
  
  // Date-only fields
  if (fieldName === 'dana_event_date' || fieldName === 'date') {
    return 'date';
  }
  
  // Datetime fields
  return 'datetime-local';
};

const RequestPageMUI = ({ 
  title, 
  fetchUrl, 
  editEndpoint, 
  deleteEndpoint, 
  statusEndpoint,
  customColumns = null,
  hiddenFields = [],
  dateFields = ['created_at', 'updated_at', 'date', 'dana_event_date', 'approved_at', 'rejected_at', 'deleted_at'],
  statusField = 'status',
  idField = 'id',
  onRowClick = null,
  onCellClick = null,
  onApprove = null,
  additionalActions = null,
  pageSize = 25,
  height = 'calc(100vh - 300px)',
  filterField = null,
  filterValue = null,
  showFilter = true,
  filterOptions = null,
  serverSideSorting = false,
  enableColumnReorder = true,
  persistToDatabase = true,
  tableId = null,
  disableActions = false, //To remove Actions column when necessary
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: pageSize,
  });
  const [sortModel, setSortModel] = useState([
    { field: dateFields[0] || 'created_at', sort: 'desc' }
  ]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [statusToUpdate, setStatusToUpdate] = useState(null);
  const [reason, setReason] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [showError, setShowError] = useState(false);
  const [columns, setColumns] = useState([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [activeFilter, setActiveFilter] = useState(filterValue);
  const [filterOpen, setFilterOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [tempColumns, setTempColumns] = useState([]);
  const [tempVisibilityModel, setTempVisibilityModel] = useState({});  
  const [userPreferences, setUserPreferences] = useState({ columnOrder: null, columnVisibility: null });
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const getTableId = () => {
    if (tableId) return tableId;
    return `table_${title.replace(/\s/g, '_').toLowerCase()}`;
  };

  // Load user preferences from database
  useEffect(() => {
    const loadPreferences = async () => {
      if (!persistToDatabase) {
        setPreferencesLoaded(true);
        return;
      }
      
      try {
        // apiClient automatically includes token via interceptor
        const preferences = await getPreferences();
        
        const tableIdentifier = getTableId();
        const columnOrderKey = `${tableIdentifier}_column_order`;
        const columnVisibilityKey = `${tableIdentifier}_column_visibility`;
        const columnWidthsKey = `${tableIdentifier}_column_widths`;
        
        let columnOrder = null;
        let columnVisibility = null;
        let columnWidths = null;
        
        const rawOrder = preferences[columnOrderKey];
        if (rawOrder !== undefined && rawOrder !== null) {
          if (typeof rawOrder === 'string') {
            try {
              columnOrder = JSON.parse(rawOrder);
            } catch (e) {
              if (rawOrder.includes(',')) {
                columnOrder = rawOrder.split(',').map(s => s.trim());
              }
            }
          } else if (Array.isArray(rawOrder)) {
            columnOrder = rawOrder;
          }
        }
        
        const rawVisibility = preferences[columnVisibilityKey];
        if (rawVisibility !== undefined && rawVisibility !== null) {
          if (typeof rawVisibility === 'string') {
            try {
              columnVisibility = JSON.parse(rawVisibility);
            } catch (e) {
              columnVisibility = null;
            }
          } else if (typeof rawVisibility === 'object' && rawVisibility !== null) {
            columnVisibility = rawVisibility;
          }
        }
        
        const rawWidths = preferences[columnWidthsKey];
        if (rawWidths !== undefined && rawWidths !== null) {
          if (typeof rawWidths === 'string') {
            try {
              columnWidths = JSON.parse(rawWidths);
            } catch (e) {
              columnWidths = null;
            }
          } else if (typeof rawWidths === 'object' && rawWidths !== null) {
            columnWidths = rawWidths;
          }
        }
        
        setUserPreferences({ columnOrder, columnVisibility, columnWidths });
      } catch (error) {
        console.error("Error loading preferences:", error);
      } finally {
        setPreferencesLoaded(true);
      }
    };
    
    loadPreferences();
  }, []);

  // Build fetch URL with pagination/filter/sort
  const buildFetchUrl = useCallback(() => {
    let url = fetchUrl;
    if (serverSideSorting) {
      const params = new URLSearchParams();
      params.append('page', paginationModel.page + 1);
      params.append('per_page', paginationModel.pageSize);
      if (sortModel.length > 0) {
        params.append('sort_by', sortModel[0].field);
        params.append('sort_order', sortModel[0].sort);
      }
      if (filterField && activeFilter !== null && activeFilter !== undefined && activeFilter !== '') {
        params.append(filterField, activeFilter);
      }
      const queryString = params.toString();
      if (queryString) {
        const separator = url.includes('?') ? '&' : '?';   // ✅ key line
        url = `${url}${separator}${queryString}`;
      }
    } else {
      // also fix client‑side filtering
      if (filterField && activeFilter !== null && activeFilter !== undefined && activeFilter !== '') {
        const separator = url.includes('?') ? '&' : '?';
        url = `${url}${separator}${filterField}=${activeFilter}`;
      }
    }
    return url;
  }, [fetchUrl, serverSideSorting, paginationModel, sortModel, filterField, activeFilter]);

  // Fetch data from API  
  const fetchData = useCallback(async () => {
    setLoading(true);

    const startedAt = performance.now();
    let requestUrl = "";

    try {
      requestUrl = buildFetchUrl();

      const response = await apiClient.get(requestUrl);
      const json = response.data;

      console.info("[API timing]", {
        url: requestUrl,
        durationMs: Math.round(performance.now() - startedAt),
        status: response.status,
      });

      if (serverSideSorting) {
        let fetchedData = [], total = 0;

        if (json?.data?.data && Array.isArray(json.data.data)) {
          fetchedData = json.data.data;
          total = json.data.total || 0;
        } else if (json?.data && Array.isArray(json.data)) {
          fetchedData = json.data;
          total = json.data.length;
        } else if (Array.isArray(json)) {
          fetchedData = json;
          total = json.length;
        }

        setData(fetchedData);
        setTotalCount(total);
      } else {
        const fetchedData =
          json?.data?.data || json?.data || json || [];

        setData(Array.isArray(fetchedData) ? fetchedData : []);
        setTotalCount(null);
      }

      setDataLoaded(true);
    } catch (err) {
      console.error("[API fetch failed]", {
        url: requestUrl,
        durationMs: Math.round(performance.now() - startedAt),
        message: err.message,
        code: err.code,
        status: err.response?.status,
        responseData: err.response?.data,
      });

      Swal.fire("Error", "Could not fetch data", "error");
      setDataLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [buildFetchUrl, serverSideSorting]);


  

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const loadData = async () => {
      setLoading(true);

      const startedAt = performance.now();
      let requestUrl = "";

      try {
        requestUrl = buildFetchUrl();

        const response = await apiClient.get(requestUrl, {
          signal: controller.signal,
        });

        if (!active) return;

        console.info("[API timing]", {
          url: requestUrl,
          durationMs: Math.round(performance.now() - startedAt),
          status: response.status,
        });

        const json = response.data;

        if (serverSideSorting) {
          let fetchedData = [];
          let total = 0;

          if (Array.isArray(json?.data?.data)) {
            fetchedData = json.data.data;
            total = json.data.total || 0;
          } else if (Array.isArray(json?.data)) {
            fetchedData = json.data;
            total = json.data.length;
          } else if (Array.isArray(json)) {
            fetchedData = json;
            total = json.length;
          }

          setData(fetchedData);
          setTotalCount(total);
        } else {
          const fetchedData =
            json?.data?.data || json?.data || json || [];

          setData(Array.isArray(fetchedData) ? fetchedData : []);
          setTotalCount(null);
        }

        setDataLoaded(true);
      } catch (err) {
        if (!active || err.code === "ERR_CANCELED") {
          return;
        }

        console.error("[API fetch failed]", {
          url: requestUrl,
          message: err.message,
          code: err.code,
          status: err.response?.status,
        });

        Swal.fire("Error", "Could not fetch data", "error");
        setDataLoaded(true);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      active = false;
      controller.abort();
    };
  }, [buildFetchUrl, serverSideSorting]);


  // Generate column definitions from data
  const generateColumns = useCallback(() => {
    if (data.length === 0) return [];
    const firstItem = data[0];
    const generatedColumns = [];

    // ACTIONS COLUMN - only show if not disabled, and only Edit button
    if (!disableActions) {
      generatedColumns.push({
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <Box>
            <IconButton onClick={() => handleEdit(params.row)} color="primary" size="small" title="Edit">
              <EditIcon fontSize="small" />
            </IconButton>
            {additionalActions && additionalActions(params.row)}
          </Box>
        ),
      });
    }

    Object.keys(firstItem).forEach((key) => {
      if (hiddenFields.includes(key)) return;
      
      // Custom header names mapping
      const headerNameMap = {
        'mobile_number': 'Mobile',
        'wt_number': 'WhatsApp',
        'dana_event_date': 'Dana Date',
        'payment_status': 'Payment Status',
        'status_reason': 'Reason for Last Update',
        'created_at': 'Created Timestamp',
        'updated_at': 'Updated Timestamp',
        'approved_at': 'Approved Timestamp',
        'rejected_at': 'Rejected Timestamp',
        'on_hold_at': 'On Hold Timestamp',
        'inactivated_at': 'Inactivated Timestamp',
        'paid_at': 'Paid Timestamp',
        'deleted_at': 'Deleted Timestamp',
        'source_type': 'Dana Type',
        'reference_no': 'Reference No',
        'first_name': 'First Name',
        'last_name': 'Last Name',
        'email': 'Email',
        'meal_type': 'Meal Type',
        'status': 'Status',
        'ip_address': 'IP Address',
        'source_id': 'Source ID',
        'approved_by': 'Approved By',
        'created_by': 'Created By',
      };
      
      const column = {
        field: key,
        headerName: headerNameMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        width: getColumnWidth(key),
        sortable: true,
      };
      // Status column - only render dropdown if statusEndpoint is provided
      if (key === statusField && statusEndpoint) {
        column.renderCell = (params) => {
          const currentStatus = params.value || '';
          const displayValue = capitalizeStatus(currentStatus);
          return (
            <Select value={displayValue} size="small" onChange={(e) => handleStatusChange(params.row, e.target.value)} sx={{ minWidth: 120 }}>
              {statusOptions.map((s) => <MenuItem key={s} value={s}>{getStatusChip(s)}</MenuItem>)}
            </Select>
          );
        };
        column.width = 150;
      } else if (key === 'meal_type') {
        column.renderCell = (params) => <Chip label={formatMealType(params.value)} color={params.value === 'breakfast' ? 'warning' : 'info'} size="small" />;
        column.width = 120;
      } else if (key === 'source_type') {
        column.renderCell = (params) => formatSourceType(params.value);
        column.width = 130;
      } else if (key === 'reference_no') {
        column.renderCell = (params) => formatReferenceNo(params.value, params.row);
        column.width = 150;
      } else if (key === "pirikara_type") {
        column.renderCell = (params) => (
          <Tooltip title="Click to view full text" arrow>
            <Box
              sx={{
                width: "100%",
                cursor: "pointer",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              {params.value || ""}
            </Box>
          </Tooltip>
        );

        column.width = 300;
      } else if (key === 'created_by') {
        column.renderCell = (params) => {
          // Handle created_by which can be an object with name property
          if (params.value && typeof params.value === 'object') {
            return params.value.name || 'Unknown';
          }
          return params.value || 'Unknown';
        };
        column.width = 150;
      } else if (dateFields.includes(key)) {
        // Special handling for dana_event_date - always show date-only
        if (key === 'dana_event_date') {
          column.valueFormatter = (value) => {
            if (!value) return '';
            try {
              const date = new Date(value);
              if (isNaN(date.getTime())) return '';
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            } catch (error) {
              return '';
            }
          };
        } else {
          column.valueFormatter = (value) => formatDate(value);
        }
        column.width = 180;
      } else if (typeof firstItem[key] === 'string' && firstItem[key]?.length > 50) {
        column.renderCell = (params) => <Typography variant="body2" title={params.value || ''}>{params.value?.substring(0, 50) || ''}...</Typography>;
      }
      generatedColumns.push(column);
    });
    return generatedColumns;
  }, [data, hiddenFields, statusField, dateFields, additionalActions, disableActions, statusEndpoint]);

  // Apply user preferences to columns when both data and preferences are ready
  useEffect(() => {
    if (dataLoaded && preferencesLoaded && data.length > 0) {
      let generatedCols = generateColumns();
      let finalColumns = [...generatedCols];
      let finalVisibility = {};

      // Apply column order from preferences
      if (enableColumnReorder && userPreferences.columnOrder && userPreferences.columnOrder.length > 0) {
        const orderedColumns = [];
        userPreferences.columnOrder.forEach(field => {
          const col = generatedCols.find(c => c.field === field);
          if (col) orderedColumns.push(col);
        });
        generatedCols.forEach(col => {
          if (!orderedColumns.find(c => c.field === col.field)) orderedColumns.push(col);
        });
        finalColumns = orderedColumns;
      }

      // Apply column visibility from preferences
      if (userPreferences.columnVisibility && Object.keys(userPreferences.columnVisibility).length > 0) {
        finalVisibility = userPreferences.columnVisibility;
      } else {
        finalColumns.forEach(col => { finalVisibility[col.field] = true; });
      }
      
      // ✅ Apply saved column widths from preferences
      if (userPreferences.columnWidths && Object.keys(userPreferences.columnWidths).length > 0) {
        finalColumns = finalColumns.map(col => {
          const savedWidth = userPreferences.columnWidths[col.field];
          return savedWidth ? { ...col, width: savedWidth } : col;
        });
      }
      
      setColumns(finalColumns);
      setColumnVisibilityModel(finalVisibility);
      setTempColumns([...finalColumns]);
      setTempVisibilityModel({ ...finalVisibility });
    }
  }, [dataLoaded, preferencesLoaded, data, userPreferences, customColumns, generateColumns, enableColumnReorder]);

  // Save preferences to database - only updates provided preferences
  const saveAllPreferences = async (columnOrder, columnVisibility, columnWidths = null) => {
    if (!persistToDatabase) return false;
    try {
      const tableIdentifier = getTableId();
      
      // First, get existing preferences to merge with new ones
      let existingPreferences = {};
      try {
        const response = await apiClient.get('/preferences');
        existingPreferences = response.data; // Axios response data
      } catch (error) {
        console.error("Error fetching existing preferences:", error);
        // If GET fails (e.g., no preferences yet), treat as empty object
      }
      
      // Create updated preferences object
      const updatedPreferences = { ...existingPreferences };
      
      // Only update provided preferences
      if (columnOrder !== undefined && columnOrder !== null) {
        updatedPreferences[`${tableIdentifier}_column_order`] = JSON.stringify(columnOrder);
      }
      if (columnVisibility !== undefined && columnVisibility !== null) {
        updatedPreferences[`${tableIdentifier}_column_visibility`] = JSON.stringify(columnVisibility);
      }
      if (columnWidths !== undefined && columnWidths !== null) {
        updatedPreferences[`${tableIdentifier}_column_widths`] = JSON.stringify(columnWidths);
      }
      
      // Save updated preferences
      await apiClient.post('/preferences', { preferences: updatedPreferences });
      
      // Update local state
      setUserPreferences(prev => ({ 
        ...prev, 
        columnOrder: columnOrder !== undefined && columnOrder !== null ? columnOrder : prev.columnOrder,
        columnVisibility: columnVisibility !== undefined && columnVisibility !== null ? columnVisibility : prev.columnVisibility,
        columnWidths: columnWidths !== undefined && columnWidths !== null ? columnWidths : prev.columnWidths
      }));
      return true;
    } catch (error) {
      console.error("Error saving preferences:", error);
      return false;
    }
  };

  const openColumnManager = () => {
    const visibleColumns = columns.filter(col => columnVisibilityModel[col.field] !== false);
    setTempColumns(visibleColumns);
    setTempVisibilityModel({ ...columnVisibilityModel });
    setShowColumnManager(true);
  };

  const saveColumnSettings = async () => {
    const orderFields = tempColumns.map(col => col.field);
    const success = await saveAllPreferences(orderFields, tempVisibilityModel);
    if (success) {
      setColumns(tempColumns);
      setColumnVisibilityModel(tempVisibilityModel);
      setShowColumnManager(false);
      Swal.fire("Success", "Column settings saved successfully", "success");
    } else {
      Swal.fire("Error", "Failed to save column settings", "error");
    }
  };

  const saveColumnWidths = async () => {
    if (!persistToDatabase) {
      Swal.fire("Info", "Column width persistence is disabled for this table", "info");
      return;
    }
    
    // Collect current column widths from the columns state
    const columnWidths = {};
    columns.forEach(col => {
      columnWidths[col.field] = col.width;
    });
    
    // Get current column order and visibility to preserve them
    const columnOrder = columns.map(col => col.field);
    const columnVisibility = { ...columnVisibilityModel };
    
    const success = await saveAllPreferences(columnOrder, columnVisibility, columnWidths);
    if (success) {
      Swal.fire("Success", "Column widths saved successfully", "success");
    } else {
      Swal.fire("Error", "Failed to save column widths", "error");
    }
  };

  const resetToDefaultSettings = async () => {
    Swal.fire({
      title: "Reset to Default?",
      text: "This will reset all column settings (order, visibility, and widths) to default.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, reset it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        const defaultColumns = generateColumns();
        const defaultOrder = defaultColumns.map(col => col.field);
        const defaultVisibility = {};
        defaultColumns.forEach(col => { defaultVisibility[col.field] = true; });
        const success = await saveAllPreferences(defaultOrder, defaultVisibility, {});
        if (success) {
          setColumns(defaultColumns);
          setTempColumns([...defaultColumns]);
          setColumnVisibilityModel(defaultVisibility);
          setTempVisibilityModel({ ...defaultVisibility });
          Swal.fire("Reset Complete", "Column settings have been reset to default", "success");
        } else {
          Swal.fire("Error", "Failed to reset column settings", "error");
        }
      }
    });
  };

  const handleTempHideColumn = (columnField) => {
    const updatedColumns = tempColumns.filter(col => col.field !== columnField);
    setTempColumns(updatedColumns);
    setTempVisibilityModel({ ...tempVisibilityModel, [columnField]: false });
  };

  const handleTempShowColumn = (columnField) => {
    const originalColumn = generateColumns().find(col => col.field === columnField);
    if (originalColumn) {
      const updatedColumns = [...tempColumns, originalColumn];
      setTempColumns(updatedColumns);
      const newModel = { ...tempVisibilityModel };
      delete newModel[columnField];
      setTempVisibilityModel(newModel);
    }
  };

  const handleTempColumnOrderChange = (newColumns) => {
    setTempColumns(newColumns);
  };

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newModel) => {
    setSortModel(newModel);
    if (serverSideSorting) {
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    }
  };

  const handleView = (row) => {
    setViewData(row);
    setOpenViewDialog(true);
  };

  const handleEdit = (row) => {
    setEditData(row);
    setOpenDialog(true);
  };

  const handleEditSubmit = async () => {
    try {
      await apiClient.put(`${editEndpoint}/${editData[idField]}`, editData);
      Swal.fire("Success", "Data updated", "success");
      setOpenDialog(false);
      fetchData();
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || err.message, "error");
    }
  };

  const handleDelete = async (item) => {
    Swal.fire({
      icon: "warning",
      title: "Are you sure?",
      text: `You are about to delete this entry.`,
      showCancelButton: true,
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          await apiClient.delete(`${deleteEndpoint}/${item[idField]}`);
          Swal.fire("Deleted!", "The record has been removed.", "success");
          fetchData();
        } catch (err) {
          Swal.fire("Error", "Failed to delete", "error");
        }
      }
    });
  };

  const handleStatusChange = (item, newStatus) => {
    const currentStatus = item[statusField] || '';
    if (currentStatus.toLowerCase() !== newStatus.toLowerCase()) {
      // If this is an approval and an onApprove handler is provided, use it
      if (newStatus === 'Approved' && onApprove) {
        onApprove(item, newStatus);
      } else {
        setStatusToUpdate({ ...item, newStatus });
        setModalOpen(true);
      }
    }
  };

  const handleConfirmStatusChange = async () => {
    if (isSubmitting) return;
    if (!reason.trim()) {
      setShowError(true);
      return;
    }
    try {
      setIsSubmitting(true);
      const payload = { status: statusToUpdate.newStatus, status_reason: reason };
      await apiClient.put(`${statusEndpoint}/${statusToUpdate[idField]}/status`, payload);

      // Success
      Swal.fire("Success", "Status updated", "success");
      setModalOpen(false);
      setReason("");
      setStatusToUpdate(null);
      setShowError(false);
      fetchData();
    } catch (err) {
      // Close reason dialog before showing error
      setModalOpen(false);
      setReason("");
      setStatusToUpdate(null);
      setShowError(false);
      Swal.fire("Error", err.response?.data?.message || err.message || "Something went wrong", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (value) => {
    if (value === null || value === undefined || value === '') return '';
    try {
      if (typeof value === 'string') {
        // Clean up the string first - remove any trailing .000Z or similar
        let cleanValue = value;
        if (cleanValue.includes('.') && (cleanValue.includes('Z') || cleanValue.includes('+'))) {
          cleanValue = cleanValue.split('.')[0];
          cleanValue = cleanValue.replace('Z', '');
        }
        
        if (/^\d{4}-\d{2}-\d{2}$/.test(cleanValue)) {
          const [year, month, day] = cleanValue.split('-');
          return `${year}-${month}-${day}`;
        }
        if (cleanValue.includes('T')) {
          const [datePart, timePart] = cleanValue.split('T');
          const time = timePart.split('.')[0];
          return `${datePart}, ${time}`;
        }
        if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(cleanValue)) {
          const [datePart, timePart] = cleanValue.split(' ');
          return `${datePart}, ${timePart}`;
        }
        const date = new Date(cleanValue);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          if (cleanValue.includes('T') || cleanValue.includes(':') || (date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0)) {
            return `${year}-${month}-${day}, ${hours}:${minutes}:${seconds}`;
          }
          return `${year}-${month}-${day}`;
        }
        return cleanValue;
      }
      if (value instanceof Date && !isNaN(value.getTime())) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        const hours = String(value.getHours()).padStart(2, '0');
        const minutes = String(value.getMinutes()).padStart(2, '0');
        const seconds = String(value.getSeconds()).padStart(2, '0');
        if (value.getHours() !== 0 || value.getMinutes() !== 0 || value.getSeconds() !== 0) {
          return `${year}-${month}-${day}, ${hours}:${minutes}:${seconds}`;
        }
        return `${year}-${month}-${day}`;
      }
      return String(value);
    } catch (error) {
      return String(value);
    }
  };

  const getStatusChip = (status) => {
    if (!status) return <Chip label="N/A" size="small" />;
    const colors = { Approved: 'success', Pending: 'warning', Rejected: 'error',  Completed: 'info'};
    const displayStatus = capitalizeStatus(status);
    return <Chip label={displayStatus} color={colors[displayStatus] || 'default'} size="small" />;
  };

  const getColumnWidth = (key) => {
    const widths = {
      id: 70, first_name: 130, last_name: 130, mobile_number: 130, wt_number: 130,
      whatsapp_number: 130, email: 200, email_address: 200, dana_event_date: 130,
      date: 130, time: 100, sanga_members_count: 130, status: 120, created_at: 180,
      updated_at: 180, ip_address: 130, status_reason: 250, meal_type: 120,
      enquiry_type: 150, enquiry_description: 300, full_name: 150, contact_number: 130,
      // New fields for dana requests
      reference_no: 150, source_type: 130, source_id: 100,
      approved_by: 120, approved_at: 180, rejected_by: 120, rejected_at: 180,
      // Payment fields
      payment_reference: 160, requested_amount: 120, paid_amount: 120, paid_at: 180,
      payer_first_name: 130, payer_last_name: 130, payer_email: 200, payer_mobile_number: 130,
    };
    return widths[key] || 150;
  };

  const handleRowClick = (params) => {
    if (onRowClick) onRowClick(params.row);
  };

  const handleCellClick = (params) => {
    if (onCellClick) {
      onCellClick(params.row, params.field);
    }
  };

  const handleFilterChange = (event) => {
    setActiveFilter(event.target.value);
    if (serverSideSorting) setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const clearFilter = () => {
    setActiveFilter(null);
    setFilterOpen(false);
    if (serverSideSorting) setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const getFilterLabel = () => {
    if (!filterField) return null;
    if (filterField === 'meal_type') {
      return activeFilter === 'breakfast' ? 'Breakfast' : activeFilter === 'lunch' ? 'Lunch' : 'All Dana Requests';
    }
    return filterOptions?.find(opt => opt.value === activeFilter)?.label || 'All';
  };

  const isServerMode = serverSideSorting;
  const displayTotal = isServerMode && totalCount !== null ? totalCount : data.length;

  // Column Manager Dialog
  const ColumnManagerDialog = () => (
    <Dialog open={showColumnManager} onClose={() => setShowColumnManager(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: '#f0f0f0', borderBottom: '1px solid #ddd' }}>
        Manage Columns
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>Visible Columns (Drag to reorder)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Drag and drop to reorder. Click the eye icon to hide a column.</Typography>
          <Box sx={{ mb: 4, maxHeight: '300px', overflowY: 'auto', border: '1px solid #ccc', borderRadius: 1, p: 1 }}>
            {tempColumns.map((column, index) => (
              <Box key={column.field} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, mb: 1, border: '1px solid #e0e0e0', borderRadius: 1, cursor: 'move', backgroundColor: '#fafafa' }}
                draggable={enableColumnReorder}
                onDragStart={(e) => e.dataTransfer.setData('text/plain', index)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  if (!enableColumnReorder) return;
                  e.preventDefault();
                  const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
                  const toIndex = index;
                  const newColumns = [...tempColumns];
                  const [movedColumn] = newColumns.splice(fromIndex, 1);
                  newColumns.splice(toIndex, 0, movedColumn);
                  handleTempColumnOrderChange(newColumns);
                }}>
                <Typography><strong>{column.headerName}</strong> ({column.field})</Typography>
                <IconButton size="small" onClick={() => handleTempHideColumn(column.field)} title="Hide column" color="error"><VisibilityOffIcon /></IconButton>
              </Box>
            ))}
          </Box>
          
          <Typography variant="subtitle1" gutterBottom>Hidden Columns</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Click the eye icon to show these columns again.</Typography>
          <Box sx={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ccc', borderRadius: 1, p: 1 }}>
            {(() => {
              const allPossible = generateColumns();
              const hiddenCols = allPossible.filter(col => !tempColumns.find(c => c.field === col.field));
              return hiddenCols.length > 0 ? hiddenCols.map(column => (
                <Box key={column.field} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, mb: 1, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f5f5f5' }}>
                  <Typography color="text.secondary"><strong>{column.headerName}</strong> ({column.field})</Typography>
                  <IconButton size="small" onClick={() => handleTempShowColumn(column.field)} title="Show column" color="primary"><VisibilityIcon /></IconButton>
                </Box>
              )) : <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>No hidden columns. All columns are visible.</Typography>;
            })()}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={resetToDefaultSettings} color="warning">Default Settings</Button>
        <Button onClick={() => setShowColumnManager(false)}>Close</Button>
        <Button onClick={saveColumnSettings} variant="contained" color="primary">Save Settings</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h5" component="div">{title} ({displayTotal} records)</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {enableColumnReorder && (
                <>
                  <Button variant="outlined" onClick={openColumnManager} startIcon={<ViewColumnIcon />} size="small">Manage Columns</Button>
                  <Button variant="outlined" onClick={saveColumnWidths} startIcon={<SaveIcon />} size="small" title="Save current column widths">Save Widths</Button>
                </>
              )}
              {showFilter && filterField && (
                <>
                  {filterOpen ? (
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>Filter</InputLabel>
                      <Select value={activeFilter || ''} onChange={handleFilterChange} label="Filter" autoFocus>
                        {filterOptions ? (
                          filterOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)
                        ) : filterField === 'meal_type' ? (
                          <>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="breakfast">Breakfast</MenuItem>
                            <MenuItem value="lunch">Lunch</MenuItem>
                          </>
                        ) : (
                          <MenuItem value="">All</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  ) : (
                    <Button variant="outlined" onClick={() => setFilterOpen(true)} startIcon={<FilterListIcon />} size="small">
                      {getFilterLabel() !== 'All Dana Requests' ? getFilterLabel() : 'Filter'}
                    </Button>
                  )}
                  {activeFilter !== null && activeFilter !== undefined && activeFilter !== '' && !filterOpen && (
                    <Button variant="text" onClick={clearFilter} size="small" color="error">Clear</Button>
                  )}
                </>
              )}
              <Button variant="outlined" onClick={fetchData} startIcon={<RefreshIcon />} disabled={loading} size="small">Refresh</Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {(loading || (data.length > 0 && columns.length === 0)) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && data.length > 0 && columns.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: "text.secondary" }}>All Records</Typography>
            <hr />
            <Box sx={{ height, width: '100%', mt: 2 }}>
              <DataGrid
                rows={data}
                columns={columns}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                rowCount={isServerMode ? totalCount : undefined}
                loading={loading}
                paginationMode={isServerMode ? "server" : "client"}
                sortingMode={isServerMode ? "server" : "client"}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[10, 15, 25, 50, 100]}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                disableRowSelectionOnClick
                getRowId={(row) => row[idField] || Math.random()}
                onRowClick={handleRowClick}
                onCellClick={handleCellClick}
                rowHeight={40}
                columnResizeMode="onChange"
                onColumnWidthChange={(params) => {
                  // Update columns state when user resizes columns
                  if (params.colDef.field && params.width) {
                    setColumns(prevColumns => 
                      prevColumns.map(col => 
                        col.field === params.colDef.field 
                          ? { ...col, width: params.width } 
                          : col
                      )
                    );
                  }
                }}
                sx={{
                  '& .MuiDataGrid-cell': { py: 0.5, borderRight: '1px solid #e0e0e0', fontSize: '0.875rem' },
                  '& .MuiDataGrid-columnHeaders': { borderBottom: '2px solid #e0e0e0', backgroundColor: '#f5f5f5', fontSize: '0.875rem', fontWeight: 600 },
                  '& .MuiDataGrid-columnSeparator': { display: 'block' },
                  '& .MuiDataGrid-cell:last-child': { borderRight: 'none' },
                  '& .MuiDataGrid-columnHeader': { borderRight: '1px solid #e0e0e0' },
                  '& .MuiDataGrid-columnHeader:last-child': { borderRight: 'none' },
                  '& .MuiDataGrid-virtualScroller': { minHeight: '200px' },
                  '& .MuiDataGrid-columnSeparator--resizable': { 
                    cursor: 'col-resize',
                    '&:hover': { backgroundColor: '#1976d2' }
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {!loading && data.length === 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography align="center" sx={{ py: 4 }}>No data found.</Typography>
          </CardContent>
        </Card>
      )}

      <ColumnManagerDialog />

      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>View Details</DialogTitle>
        <DialogContent dividers>
          {viewData && (
            <Box sx={{ pt: 2 }}>
              {Object.entries(viewData).filter(([key]) => !hiddenFields.includes(key)).map(([key, value]) => {
                let displayValue = value?.toString() || 'N/A';
                
                if (key === 'meal_type') {
                  displayValue = formatMealType(value);
                } else if (key === 'source_type') {
                  displayValue = formatSourceType(value);
                } else if (key === 'reference_no') {
                  displayValue = formatReferenceNo(value, viewData);
                } else if (key === 'status') {
                  displayValue = capitalizeStatus(value);
                } else if (key === 'dana_event_date') {
                  // Show date-only format for dana_event_date
                  try {
                    const date = new Date(value);
                    if (!isNaN(date.getTime())) {
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      displayValue = `${year}-${month}-${day}`;
                    }
                  } catch (error) {
                    displayValue = formatDate(value);
                  }
                } else if (dateFields.includes(key)) {
                  displayValue = formatDate(value);
                }
                
                return (
                  <Box key={key} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                    </Typography>
                    <Typography variant="body1">
                      {displayValue}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={modalOpen} onClose={() => !isSubmitting && setModalOpen(false)}>
        <DialogTitle>Reason for Status Change</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason"
            fullWidth
            multiline
            rows={3}
            value={reason}
            onChange={(e) => { setReason(e.target.value); if (e.target.value.trim()) setShowError(false); }}
            required
            disabled={isSubmitting}
            error={showError}
            helperText={showError ? 'Reason is required' : ''}
            sx={{ mt: 2 }}
          />
          {isSubmitting && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="textSecondary">Processing status change...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setModalOpen(false); setShowError(false); setReason(""); }} disabled={isSubmitting}>Cancel</Button>
          <Button onClick={handleConfirmStatusChange} variant="contained" disabled={!reason.trim() || isSubmitting}>
            {isSubmitting ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Entry</DialogTitle>
        <DialogContent>
          {editData && Object.entries(editData)
            .filter(([key]) => !hiddenFields.includes(key) && key !== idField && key !== 'created_at' && key !== 'updated_at' && key !== 'status' && key !== 'deleted_at' && key !== 'approved_at' && key !== 'rejected_at')
            .map(([key, value]) => {
              // Define which fields should be read-only
              const readOnlyFields = [
                'reference_no', // auto-generated
                'source_type', // should not be changed after creation
                'source_id', // foreign key
                'approved_by', 'approved_at', 'rejected_by', 'rejected_at', // audit fields
                'ip_address', // should not be changed
                'meal_type', // should not be changed after creation (affects reference number)
                'status', // should only be changed via status dropdown in grid (triggers email workflow)
                'deleted_at', // soft delete timestamp, should not be manually edited
              ];
              
              const isReadOnly = readOnlyFields.includes(key);
              
              if (key === 'meal_type') {
                return (
                  <FormControl fullWidth margin="dense" size="small" key={key}>
                    <InputLabel>{key.replace(/_/g, " ")}</InputLabel>
                    <Select 
                      value={value || ''} 
                      label={key.replace(/_/g, " ")} 
                      onChange={(e) => setEditData(prev => ({ ...prev, [key]: e.target.value }))}
                      disabled={true} // meal_type should be read-only (affects reference number)
                    >
                      <MenuItem value="breakfast">Breakfast</MenuItem>
                      <MenuItem value="lunch">Lunch</MenuItem>
                    </Select>
                  </FormControl>
                );
              } else if (key === 'source_type') {
                return (
                  <FormControl fullWidth margin="dense" size="small" key={key}>
                    <InputLabel>{key.replace(/_/g, " ")}</InputLabel>
                    <Select 
                      value={value || ''} 
                      label={key.replace(/_/g, " ")} 
                      onChange={(e) => setEditData(prev => ({ ...prev, [key]: e.target.value }))}
                      disabled={true} // source_type should be read-only
                    >
                      <MenuItem value="direct">Day Dana</MenuItem>
                      <MenuItem value="annual">Annual Dana</MenuItem>
                      <MenuItem value="standby">Standby Dana</MenuItem>
                    </Select>
                  </FormControl>
                );
              }
              return (
                <TextField
                  key={key}
                  label={key.replace(/_/g, " ")}
                  value={dateFields.includes(key) ? formatDateForInput(value, key) : (value || '')}
                  fullWidth
                  margin="dense"
                  onChange={(e) => setEditData(prev => ({ ...prev, [key]: e.target.value }))}
                  type={getInputTypeForField(key, dateFields)}
                  InputLabelProps={dateFields.includes(key) ? { shrink: true } : {}}
                  size="small"
                  disabled={isReadOnly}
                  InputProps={{
                    readOnly: isReadOnly,
                  }}
                />
              );
            })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RequestPageMUI;